import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  HStack,
  Icon,
  useColorModeValue
} from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';

interface SessionWarningDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onExtendSession: () => void;
  onLogout: () => void;
  warningTimeSeconds?: number;
}

export const SessionWarningDialog: React.FC<SessionWarningDialogProps> = ({
  isOpen,
  onClose,
  onExtendSession,
  onLogout,
  warningTimeSeconds = 120 // 2 minutes default
}) => {
  const [timeLeft, setTimeLeft] = useState(warningTimeSeconds);
  const bgColor = useColorModeValue('white', 'gray.800');

  useEffect(() => {
    if (!isOpen) {
      setTimeLeft(warningTimeSeconds);
      return;
    }

    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Time's up - auto logout
          clearInterval(interval);
          onLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, warningTimeSeconds, onLogout]);

  const handleExtendSession = () => {
    onExtendSession();
    onClose();
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={false}>
      <ModalOverlay bg="rgba(0, 0, 0, 0.6)" />
      <ModalContent bg={bgColor} mx={4}>
        <ModalHeader>
          <HStack spacing={3}>
            <Icon as={WarningIcon} color="orange.400" boxSize={6} />
            <Text>Session Timeout Warning</Text>
          </HStack>
        </ModalHeader>
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text>
              Your session will expire due to inactivity. You will be automatically 
              logged out in:
            </Text>
            
            <Text 
              fontSize="2xl" 
              fontWeight="bold" 
              textAlign="center"
              color={timeLeft <= 30 ? "red.500" : "orange.500"}
            >
              {formatTime(timeLeft)}
            </Text>
            

          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3} width="100%">
            <Button
              variant="outline"
              onClick={onLogout}
              width="50%"
            >
              Logout Now
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleExtendSession}
              width="50%"
            >
              Stay Logged In
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SessionWarningDialog;